import React, { useState, useRef, useEffect } from "react";
import { bookingOptions } from "../data/mockData";
import { X, Calendar, Clock, Truck, Send, CheckCircle, AlertCircle } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";

// Das Formular wird vom PHP-Skript auf demselben Host verarbeitet (public/api/reservation.php)
const RESERVATION_ENDPOINT = "/api/reservation.php";

const BookingModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    type: "",
    height: "",
    drive: "",
    dateFrom: "",
    dateTo: "",
    timeFrom: "08:00",
    timeTo: "17:00",
    name: "",
    address: "",
    phone: "",
    email: "",
    siteAddress: "",
    message: "",
    website: "", // Honeypot – muss leer bleiben
  });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Zeit-Falle: Zeitpunkt merken, an dem das Formular geöffnet wurde
  const openedAtRef = useRef(0);
  useEffect(() => {
    if (isOpen) openedAtRef.current = Date.now();
  }, [isOpen]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await axios.post(RESERVATION_ENDPOINT, {
        type: formData.type,
        height: formData.height,
        drive: formData.drive,
        dateFrom: formData.dateFrom,
        dateTo: formData.dateTo,
        timeFrom: formData.timeFrom,
        timeTo: formData.timeTo,
        name: formData.name,
        address: formData.address,
        phone: formData.phone,
        email: formData.email,
        siteAddress: formData.siteAddress,
        message: formData.message,
        website: formData.website, // Honeypot
        elapsed: Date.now() - (openedAtRef.current || Date.now()), // ms seit Öffnen
      });
      setSubmitted(true);
      setTimeout(() => {
        setSubmitted(false);
        onClose();
        setFormData({
          type: "",
          height: "",
          drive: "",
          dateFrom: "",
          dateTo: "",
          timeFrom: "08:00",
          timeTo: "17:00",
          name: "",
          address: "",
          phone: "",
          email: "",
          siteAddress: "",
          message: "",
          website: "",
        });
      }, 2500);
    } catch (err) {
      console.error("Reservation error:", err);
      setError("Fehler beim Senden. Bitte versuchen Sie es erneut.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[60] flex items-center justify-center p-4"
        >
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.3 }}
            className="relative w-full max-w-lg bg-white rounded-3xl overflow-hidden shadow-2xl max-h-[90vh] overflow-y-auto"
          >
            {submitted ? (
              <div className="p-12 text-center">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", stiffness: 200 }}
                >
                  <CheckCircle size={56} className="text-[#4a7c59] mx-auto mb-4" />
                </motion.div>
                <h3 className="text-2xl font-bold text-[#1d1d1f] mb-2">
                  Anfrage gesendet!
                </h3>
                <p className="text-sm text-[#6e6e73]">
                  Wir melden uns in Kürze bei Ihnen.
                </p>
              </div>
            ) : (
              <>
                {/* Header */}
                <div className="flex items-center justify-between p-6 pb-0">
                  <div>
                    <h3 className="text-xl font-bold text-[#1d1d1f] tracking-tight">
                      Jetzt Reservieren
                    </h3>
                    <p className="text-xs text-[#6e6e73] mt-1">
                      Unverbindliche Anfrage
                    </p>
                  </div>
                  <button
                    onClick={onClose}
                    className="w-8 h-8 rounded-full bg-[#f5f5f7] flex items-center justify-center hover:bg-[#e5e5e5] transition-colors"
                  >
                    <X size={16} className="text-[#6e6e73]" />
                  </button>
                </div>

                <div className="mx-6 mt-4 bg-[#fef9ec] border border-[#f5e6b8] rounded-xl px-4 py-3 flex items-center gap-2.5">
                  <AlertCircle size={16} className="text-[#b8860b] shrink-0" />
                  <p className="text-xs text-[#92700c] font-medium">
                    Ihre Anfrage wird telefonisch oder schriftlich bestätigt.
                  </p>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="p-6 space-y-4">
                  {/* Honeypot – für Menschen unsichtbar, fängt Bots ab. Nicht entfernen. */}
                  <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", top: "-9999px", width: 1, height: 1, overflow: "hidden" }}>
                    <label>
                      Website (bitte leer lassen)
                      <input
                        type="text"
                        name="website"
                        tabIndex={-1}
                        autoComplete="off"
                        value={formData.website}
                        onChange={(e) => handleChange("website", e.target.value)}
                      />
                    </label>
                  </div>

                  {/* Equipment Type */}
                  <div>
                    <label className="text-xs font-medium text-[#6e6e73] mb-1.5 flex items-center gap-1.5">
                      <Truck size={12} /> Bühne auswählen
                    </label>
                    <select
                      value={formData.type}
                      onChange={(e) => handleChange("type", e.target.value)}
                      required
                      className="w-full bg-[#f5f5f7] border-0 rounded-xl px-4 py-3 text-sm text-[#1d1d1f] focus:ring-2 focus:ring-[#4a7c59]/30 focus:outline-none appearance-none"
                    >
                      <option value="">Bitte wählen...</option>
                      {bookingOptions.types.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Height & Drive */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-medium text-[#6e6e73] mb-1.5 block">
                        Arbeitshöhe / Auslage
                      </label>
                      <input
                        type="text"
                        value={formData.height}
                        onChange={(e) => handleChange("height", e.target.value)}
                        placeholder="z.B. 20m"
                        className="w-full bg-[#f5f5f7] border-0 rounded-xl px-4 py-3 text-sm text-[#1d1d1f] focus:ring-2 focus:ring-[#4a7c59]/30 focus:outline-none placeholder:text-[#aeaeb2]"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-[#6e6e73] mb-1.5 block">
                        Antrieb
                      </label>
                      <select
                        value={formData.drive}
                        onChange={(e) => handleChange("drive", e.target.value)}
                        className="w-full bg-[#f5f5f7] border-0 rounded-xl px-4 py-3 text-sm text-[#1d1d1f] focus:ring-2 focus:ring-[#4a7c59]/30 focus:outline-none appearance-none"
                      >
                        <option value="">Bitte wählen</option>
                        <option value="diesel">Diesel</option>
                        <option value="elektro">Elektro</option>
                        <option value="hybrid">Hybrid</option>
                      </select>
                    </div>
                  </div>

                  {/* Dates */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-medium text-[#6e6e73] mb-1.5 flex items-center gap-1.5">
                        <Calendar size={12} /> Von
                      </label>
                      <input
                        type="date"
                        value={formData.dateFrom}
                        onChange={(e) => handleChange("dateFrom", e.target.value)}
                        required
                        className="w-full bg-[#f5f5f7] border-0 rounded-xl px-4 py-3 text-sm text-[#1d1d1f] focus:ring-2 focus:ring-[#4a7c59]/30 focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-[#6e6e73] mb-1.5 flex items-center gap-1.5">
                        <Calendar size={12} /> Bis
                      </label>
                      <input
                        type="date"
                        value={formData.dateTo}
                        onChange={(e) => handleChange("dateTo", e.target.value)}
                        required
                        className="w-full bg-[#f5f5f7] border-0 rounded-xl px-4 py-3 text-sm text-[#1d1d1f] focus:ring-2 focus:ring-[#4a7c59]/30 focus:outline-none"
                      />
                    </div>
                  </div>

                  {/* Times */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-medium text-[#6e6e73] mb-1.5 flex items-center gap-1.5">
                        <Clock size={12} /> Uhrzeit von
                      </label>
                      <select
                        value={formData.timeFrom}
                        onChange={(e) => handleChange("timeFrom", e.target.value)}
                        className="w-full bg-[#f5f5f7] border-0 rounded-xl px-4 py-3 text-sm text-[#1d1d1f] focus:ring-2 focus:ring-[#4a7c59]/30 focus:outline-none appearance-none"
                      >
                        {bookingOptions.times.map((t) => (
                          <option key={`from-${t}`} value={t}>
                            {t} Uhr
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-[#6e6e73] mb-1.5 flex items-center gap-1.5">
                        <Clock size={12} /> Uhrzeit bis
                      </label>
                      <select
                        value={formData.timeTo}
                        onChange={(e) => handleChange("timeTo", e.target.value)}
                        className="w-full bg-[#f5f5f7] border-0 rounded-xl px-4 py-3 text-sm text-[#1d1d1f] focus:ring-2 focus:ring-[#4a7c59]/30 focus:outline-none appearance-none"
                      >
                        {bookingOptions.times.map((t) => (
                          <option key={`to-${t}`} value={t}>
                            {t} Uhr
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Contact info */}
                  <div className="border-t border-[#e5e5e5] pt-4 mt-4">
                    <p className="text-xs font-medium text-[#6e6e73] mb-3">
                      Ihre Kontaktdaten
                    </p>
                    <div className="space-y-3">
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => handleChange("name", e.target.value)}
                        required
                        placeholder="Ihr Name / Firma"
                        className="w-full bg-[#f5f5f7] border-0 rounded-xl px-4 py-3 text-sm text-[#1d1d1f] focus:ring-2 focus:ring-[#4a7c59]/30 focus:outline-none placeholder:text-[#aeaeb2]"
                      />
                      <input
                        type="text"
                        value={formData.address}
                        onChange={(e) => handleChange("address", e.target.value)}
                        placeholder="Adresse"
                        className="w-full bg-[#f5f5f7] border-0 rounded-xl px-4 py-3 text-sm text-[#1d1d1f] focus:ring-2 focus:ring-[#4a7c59]/30 focus:outline-none placeholder:text-[#aeaeb2]"
                      />
                      <div className="grid grid-cols-2 gap-3">
                        <input
                          type="tel"
                          value={formData.phone}
                          onChange={(e) => handleChange("phone", e.target.value)}
                          required
                          placeholder="Telefon"
                          className="w-full bg-[#f5f5f7] border-0 rounded-xl px-4 py-3 text-sm text-[#1d1d1f] focus:ring-2 focus:ring-[#4a7c59]/30 focus:outline-none placeholder:text-[#aeaeb2]"
                        />
                        <input
                          type="email"
                          value={formData.email}
                          onChange={(e) => handleChange("email", e.target.value)}
                          placeholder="Email (optional)"
                          className="w-full bg-[#f5f5f7] border-0 rounded-xl px-4 py-3 text-sm text-[#1d1d1f] focus:ring-2 focus:ring-[#4a7c59]/30 focus:outline-none placeholder:text-[#aeaeb2]"
                        />
                      </div>
                      <input
                        type="text"
                        value={formData.siteAddress}
                        onChange={(e) => handleChange("siteAddress", e.target.value)}
                        placeholder="Baustelle / Adresse"
                        className="w-full bg-[#f5f5f7] border-0 rounded-xl px-4 py-3 text-sm text-[#1d1d1f] focus:ring-2 focus:ring-[#4a7c59]/30 focus:outline-none placeholder:text-[#aeaeb2]"
                      />
                      <textarea
                        value={formData.message}
                        onChange={(e) => handleChange("message", e.target.value)}
                        placeholder="Nachricht (optional)"
                        rows={3}
                        className="w-full bg-[#f5f5f7] border-0 rounded-xl px-4 py-3 text-sm text-[#1d1d1f] focus:ring-2 focus:ring-[#4a7c59]/30 focus:outline-none placeholder:text-[#aeaeb2] resize-none"
                      />
                    </div>
                  </div>

                  {/* Error message */}
                  {error && (
                    <div className="flex items-center gap-2 text-red-600 bg-red-50 rounded-xl px-4 py-3 text-sm">
                      <AlertCircle size={16} />
                      {error}
                    </div>
                  )}

                  {/* Submit */}
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-[#4a7c59] hover:bg-[#3d6a4a] disabled:opacity-60 disabled:cursor-not-allowed text-white py-3.5 rounded-xl text-sm font-medium transition-all duration-300 hover:shadow-lg flex items-center justify-center gap-2"
                  >
                    {loading ? (
                      <span className="flex items-center gap-2">
                        <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Wird gesendet...
                      </span>
                    ) : (
                      <>
                        <Send size={14} />
                        Jetzt Reservieren
                      </>
                    )}
                  </button>
                </form>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default BookingModal;
